/**
 * Scroll-to-Top Button Web Component
 *
 * A fixed-position button that appears when scrolling down and smoothly
 * returns users to the top of the page when clicked.
 *
 * @example
 * <scroll-to-top data-threshold="100" data-position-right="24">
 *   <button class="scroll-to-top__button">...</button>
 * </scroll-to-top>
 */
class ScrollToTop extends HTMLElement {
  constructor() {
    super();

    // Configuration from data attributes
    this.threshold = parseInt(this.dataset.threshold) || 100;
    this.positionRight = parseInt(this.dataset.positionRight) || 24;
    this.positionBottom = parseInt(this.dataset.positionBottom) || 24;
    this.buttonSize = parseInt(this.dataset.buttonSize) || 56;
    this.hideMobile = this.dataset.hideMobile === 'true';

    // State
    this.isVisible = false;
    this.handleScroll = null;
    this.button = null;
  }

  /**
   * Called when element is added to the DOM
   */
  connectedCallback() {
    this.button = this.querySelector('.scroll-to-top__button');

    if (!this.button) {
      console.warn('ScrollToTop: button not found');
      return;
    }

    // Check if page is scrollable
    const isScrollable = document.documentElement.scrollHeight > window.innerHeight;
    if (!isScrollable) {
      this.style.display = 'none';
      return;
    }

    // Apply custom CSS properties from settings
    this.applyCustomStyles();

    // Start hidden by default (class already set in markup)
    this.isVisible = false;

    // Bind events
    this.button.addEventListener('click', this.scrollToTop.bind(this));

    // Use throttle for frequent updates during scroll (every 100ms)
    if (typeof throttle === 'function') {
      this.handleScrollThrottled = throttle(this.checkScrollPosition.bind(this), 100);
    } else {
      console.warn('ScrollToTop: throttle function not found, using unthrottled handler');
      this.handleScrollThrottled = this.checkScrollPosition.bind(this);
    }

    // Use debounce to catch final position when scrolling stops (after 150ms of no scroll)
    if (typeof debounce === 'function') {
      this.handleScrollDebounced = debounce(this.checkScrollPosition.bind(this), 150);
    } else {
      this.handleScrollDebounced = this.checkScrollPosition.bind(this);
    }

    // Combine both handlers: throttle during scroll + debounce when scroll ends
    this.handleScroll = () => {
      this.handleScrollThrottled();
      this.handleScrollDebounced();
    };

    window.addEventListener('scroll', this.handleScroll, { passive: true });

    // Check initial scroll position and update visibility if needed
    this.checkScrollPosition();
  }

  /**
   * Called when element is removed from the DOM
   */
  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.scrollToTop.bind(this));
    }
    window.removeEventListener('scroll', this.handleScroll);
  }

  /**
   * Apply custom styles based on theme editor settings
   */
  applyCustomStyles() {
    // Apply settings as CSS custom properties
    this.style.setProperty('--scroll-button-size', `${this.buttonSize}px`);
    this.style.setProperty('--scroll-button-icon-size', `${this.buttonSize * 0.43}px`);
    this.style.setProperty('--scroll-button-right', `${this.positionRight}px`);
    this.style.setProperty('--scroll-button-bottom', `${this.positionBottom}px`);

    // Mobile adjustments (85% of desktop size)
    const mobileSize = Math.round(this.buttonSize * 0.85);
    const mobileIconSize = Math.round(mobileSize * 0.43);

    // Create media query for mobile
    const style = document.createElement('style');
    style.textContent = `
      @media screen and (max-width: 749px) {
        scroll-to-top {
          --scroll-button-size: ${mobileSize}px;
          --scroll-button-icon-size: ${mobileIconSize}px;
        }
      }
    `;
    this.appendChild(style);
  }

  /**
   * Check current scroll position and show/hide button
   */
  checkScrollPosition() {
    const scrollY = window.scrollY || window.pageYOffset;
    const shouldBeVisible = scrollY > this.threshold;

    if (shouldBeVisible && !this.isVisible) {
      this.show();
    } else if (!shouldBeVisible && this.isVisible) {
      this.hide();
    }
  }

  /**
   * Show the button
   */
  show() {
    this.isVisible = true;
    this.classList.add('scroll-to-top--visible');
  }

  /**
   * Hide the button
   */
  hide() {
    this.isVisible = false;
    this.classList.remove('scroll-to-top--visible');
  }

  /**
   * Scroll to top of page with smooth animation
   */
  scrollToTop() {
    // Check for smooth scroll support
    if ('scrollBehavior' in document.documentElement.style) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      // Fallback for older browsers (Safari < 15.4, IE)
      // Use requestAnimationFrame for smoother animation
      this.smoothScrollFallback();
    }
  }

  /**
   * Smooth scroll fallback for browsers without native support
   */
  smoothScrollFallback() {
    const startPosition = window.scrollY || window.pageYOffset;
    const duration = 600; // milliseconds
    const startTime = performance.now();

    /**
     * Easing function for natural movement
     * @param {number} t - Progress (0 to 1)
     * @returns {number} Eased progress
     */
    const easeInOutCubic = (t) => {
      return t < 0.5
        ? 4 * t * t * t
        : 1 - Math.pow(-2 * t + 2, 3) / 2;
    };

    const scroll = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easing = easeInOutCubic(progress);

      window.scrollTo(0, startPosition * (1 - easing));

      if (progress < 1) {
        requestAnimationFrame(scroll);
      }
    };

    requestAnimationFrame(scroll);
  }
}

// Define custom element
customElements.define('scroll-to-top', ScrollToTop);
